"use client"

import { useState, useRef } from "react"
import { X, Clipboard, Check, ClipboardList, CheckCircle } from "lucide-react"

const OrderSummaryModal = ({ isOpen, onClose, orderData }) => {
  const [copied, setCopied] = useState(false)
  const summaryRef = useRef(null)

  if (!isOpen || !orderData) return null

  const { customerDetails, orderLines, orderSummary, courierThreshold = 2000 } = orderData

  // Format address
  const formatAddress = () => {
    const addressParts = [
      customerDetails.addressLine1,
      customerDetails.addressLine2,
      customerDetails.addressLine3,
    ].filter(Boolean)

    return addressParts.join(", ")
  }

  // Generate formatted order summary text
  const generateSummaryText = () => {
    let summary = "Order Summary\n\n"
    summary += `👤 Name: ${customerDetails.customerName}\n`
    summary += `📍 Address: ${formatAddress()}\n`
    summary += `📞 Phone: ${customerDetails.phone}\n\n`

    summary += "🧴 Order Items:\n"

    orderLines.forEach((line) => {
      if (line.productId && line.quantity > 0) {
        summary += `* ${line.quantity} x ${line.productName.replace(/^\[.*?\]\s*/, "")} (${line.variation}) – Rs. ${line.lineTotal.toFixed(2)}\n`
      }
    })

    // Delivery info
    if (orderSummary.shippingCost === 0 && orderSummary.subTotal > 0) {
      summary += `🚚 Delivery: Free (Order Rs. ${courierThreshold.toLocaleString()} or more)\n\n`
    } else {
      summary += `🚚 Delivery: Rs. ${orderSummary.shippingCost.toFixed(2)} (${customerDetails.province} Province)\n\n`
    }

    summary += `Total Amount: Rs. ${orderSummary.total.toFixed(2)}\n\n`
    summary += `Please confirm the order by replying "Confirm" so we can proceed to dispatch. 😊`

    return summary
  }

  const summaryText = generateSummaryText()

  // Copy to clipboard function
  const copyToClipboard = () => {
    navigator.clipboard.writeText(summaryText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Order Summary
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Ready-to-send summary for customer</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="relative">
            <div
              ref={summaryRef}
              className="whitespace-pre-wrap bg-gray-50 border border-gray-200 p-4 rounded-xl font-mono text-xs sm:text-sm text-gray-700 max-h-[300px] overflow-y-auto mb-6 shadow-sm custom-scrollbar"
            >
              {summaryText}
            </div>
          </div>

          <div className="flex gap-3 justify-end items-center border-t border-gray-50 pt-4">
            {copied && (
              <span className="text-sm text-green-600 font-medium flex items-center gap-1.5 animate-in fade-in slide-in-from-right-2">
                <CheckCircle className="w-4 h-4" /> Copied to clipboard!
              </span>
            )}
            <button
              onClick={copyToClipboard}
              className={`flex items-center gap-2 px-5 py-2.5 font-medium rounded-xl text-white transition-all shadow-lg transform active:scale-95 ${copied
                  ? "bg-green-600 hover:bg-green-700 shadow-green-600/20"
                  : "bg-blue-600 hover:bg-blue-700 shadow-blue-600/20"
                }`}
            >
              {copied ? (
                <Check className="w-4 h-4" />
              ) : (
                <Clipboard className="w-4 h-4" />
              )}
              {copied ? "Copied" : "Copy Summary"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderSummaryModal
